import { type KeyboardEvent, type MouseEvent, useEffect, useRef, useState } from 'react';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';

export function useMobileAutocomplete() {
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // The trigger is `readOnly`, so focusing it raises no keyboard; render a KeyboardProxyInput on it.
  const keyboardProxyRef = useRef<HTMLInputElement>(null);
  const isMobile = !isDesktop;
  const close = () => setOpen(false);

  const opensPicker = (event: MouseEvent<HTMLElement>) =>
    isMobile && event.target instanceof Element && !event.target.closest('button');

  useEffect(() => {
    if (isDesktop) setOpen(false);
  }, [isDesktop]);

  return {
    isMobile,
    open: isMobile && open,
    close,
    inputRef,
    keyboardProxyRef,
    triggerProps: {
      // A first tap would otherwise focus the readOnly trigger as well, and the keyboard closes.
      onMouseDownCapture: (event: MouseEvent<HTMLElement>) => {
        if (opensPicker(event)) event.preventDefault();
      },
      onClickCapture: (event: MouseEvent<HTMLElement>) => {
        if (!opensPicker(event)) return;
        event.stopPropagation();
        keyboardProxyRef.current?.focus({ preventScroll: true });
        setOpen(true);
      },
    },
    inputProps: isMobile
      ? {
          readOnly: true,
          role: 'button',
          'aria-haspopup': 'dialog' as const,
          'aria-expanded': open,
          'aria-autocomplete': undefined,
          onKeyDownCapture: (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            setOpen(true);
          },
        }
      : {},
  };
}
